import { tv, type VariantProps } from 'tailwind-variants'

/**
 * Layout primitive used as the base for cards/panels across the app.
 *
 * Box properties (surface color, border, radius) are FIXED per `surface`.
 * Size is intrinsic: the container grows/shrinks to fit its children — there
 * is deliberately no `size` prop. Pass padding/width via `className` at the
 * call site when a specific composition needs it.
 *
 * Mapped from Figma (values read off the component SVGs):
 *   default → white fill, gray-200 border, radius 16  → rounded-lg
 *             (the loan-card / list-item surface)
 *   accent  → red-50 fill,  ~red-50 tonal border, radius 20 → rounded-xl
 *             (the "How it Works" panel)
 *   external→ white fill, primary (orange) border, radius 20 → rounded-xl
 *             (frames externally-hosted content — the NDA / application — so it
 *             reads as embedded, not a native app screen)
 *
 * NOTE:
 * - The accent panel in Figma also has faint decorative corner circles
 *   (opacity 0.4 white). That's baked-in art, not a token, and is intentionally
 *   NOT part of this primitive — add it per-instance where wanted so small
 *   accent panels aren't forced to render it.
 */
const container = tv({
  base: 'block',
  variants: {
    surface: {
      default: 'bg-white border border-gray-200 rounded-lg',
      accent: 'bg-red-50 border border-red-200 rounded-xl',
      external: 'bg-white border border-primary rounded-xl',
    },
  },
  defaultVariants: {
    surface: 'default',
  },
})

type ContainerVariants = VariantProps<typeof container>

export type ContainerProps<T extends React.ElementType = 'div'> =
  ContainerVariants & {
    /** Element to render. Defaults to <div>. */
    as?: T
    className?: string
    children?: React.ReactNode
  } & Omit<
      React.ComponentPropsWithoutRef<T>,
      'as' | 'className' | 'children'
    >

export function Container<T extends React.ElementType = 'div'>({
  as,
  surface,
  className,
  children,
  ...rest
}: ContainerProps<T>) {
  const Tag = (as ?? 'div') as React.ElementType
  return (
    <Tag className={container({ surface, className })} {...rest}>
      {children}
    </Tag>
  )
}

/**
 * Thin convenience wrapper that pins surface="accent". Lets you write
 * <AccentContainer> at call sites while keeping all box mechanics in the one
 * Container config above (single source of truth).
 */
export function AccentContainer<T extends React.ElementType = 'div'>(
  props: Omit<ContainerProps<T>, 'surface'>,
) {
  return <Container {...(props as ContainerProps<T>)} surface="accent" />
}